import React, { useState, useRef, useEffect } from 'react';
import {
  Search,
  Filter,
  RotateCcw,
  Settings2,
  Eye,
  Download,
  List,
  Grid3x3,
  Calendar,
  ChevronDown,
} from 'lucide-react';
import { GridToolbarFilter, GridAction, GridViewType } from '../../../types/datagrid';
import DatePicker from '../../forms/DatePicker';
import DateRangePicker, { DateRange } from '../../forms/DateRangePicker';

interface GridToolbarProps {
  filters: GridToolbarFilter[];
  filterValues: Record<string, any>;
  onFilterChange: (key: string, value: any) => void;
  onResetFilters: () => void;
  actions?: GridAction[];
  selectedRows: string[];
  data: any[];
  viewType: GridViewType;
  onViewTypeChange: (viewType: GridViewType) => void;
  onColumnManager: () => void;
  onViewManager: () => void;
  onExport: (format: 'csv' | 'xlsx' | 'pdf') => void;
  sticky?: boolean;
  permissions?: {
    canCreateView?: boolean;
    canEditView?: boolean;
    canDeleteView?: boolean;
    canExport?: boolean;
    canBulkEdit?: boolean;
  };
}

export const GridToolbar: React.FC<GridToolbarProps> = ({
  filters,
  filterValues,
  onFilterChange,
  onResetFilters,
  actions = [],
  selectedRows,
  data,
  viewType,
  onViewTypeChange,
  onColumnManager,
  onViewManager,
  onExport,
  sticky = true,
  permissions = {},
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showViewTypeMenu, setShowViewTypeMenu] = useState(false);

  const exportMenuRef = useRef<HTMLDivElement>(null);
  const viewTypeMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
      if (viewTypeMenuRef.current && !viewTypeMenuRef.current.contains(event.target as Node)) {
        setShowViewTypeMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasActiveFilters = Object.values(filterValues).some(
    value =>
      value !== null &&
      value !== undefined &&
      value !== '' &&
      (!Array.isArray(value) || value.length > 0)
  );

  const viewTypeIcons = {
    list: List,
    board: Grid3x3,
    calendar: Calendar,
  };

  const ViewTypeIcon = viewTypeIcons[viewType];

  const renderFilter = (filter: GridToolbarFilter) => {
    const value = filterValues[filter.id] || '';

    switch (filter.type) {
      case 'search':
        return (
          <div key={filter.id} className="relative flex-1 max-w-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={filter.placeholder || `Search ${filter.label.toLowerCase()}...`}
              value={value}
              onChange={e => onFilterChange(filter.id, e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
        );

      case 'select':
        return (
          <div key={filter.id} className="relative">
            <select
              value={value}
              onChange={e => onFilterChange(filter.id, e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="">{filter.label}</option>
              {filter.options?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        );

      case 'multiselect': {
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div key={filter.id} className="relative">
            <button
              type="button"
              className="flex items-center justify-between bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-w-32"
            >
              <span className="text-gray-700">
                {selectedValues.length === 0
                  ? filter.label
                  : selectedValues.length === 1
                    ? filter.options?.find(opt => opt.value === selectedValues[0])?.label
                    : `${selectedValues.length} selected`}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-400 ml-2" />
            </button>
            {/* Dropdown menu would be implemented here */}
          </div>
        );
      }

      case 'date':
        return (
          <div key={filter.id} className="min-w-40">
            <DatePicker
              value={value || null}
              onChange={date => onFilterChange(filter.id, date)}
              placeholder={filter.label}
              showClear
              showToday
              className="[&_input]:py-2"
            />
          </div>
        );

      case 'daterange': {
        const dateRangeValue: DateRange = value
          ? { startDate: value.startDate || null, endDate: value.endDate || null }
          : { startDate: null, endDate: null };
        return (
          <div key={filter.id} className="min-w-64">
            <DateRangePicker
              value={dateRangeValue}
              onChange={(range: DateRange) => onFilterChange(filter.id, range)}
              startPlaceholder="Start"
              endPlaceholder="End"
              compact
              showPresets
              showClear
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div
      className={`bg-white border-b border-gray-200 px-6 py-3 ${sticky ? 'sticky top-0 z-10' : ''}`}
    >
      <div className="flex items-center justify-between space-x-4">
        {/* Left side - Filters */}
        <div className="flex items-center space-x-4 flex-1">
          {filters.map(renderFilter)}

          {/* Reset Filters */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              title="Reset all filters"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Reset</span>
            </button>
          )}
        </div>

        {/* Right side - Actions and Controls */}
        <div className="flex items-center space-x-3">
          {/* Bulk Actions */}
          {selectedRows.length > 0 && actions.length > 0 && (
            <div className="flex items-center space-x-2 pr-3 border-r border-gray-200">
              <span className="text-sm text-gray-600">{selectedRows.length} selected</span>
              {actions
                .filter(action => !action.isVisible || action.isVisible(selectedRows, data))
                .map(action => (
                  <button
                    key={action.id}
                    type="button"
                    onClick={() => action.onClick(selectedRows, data)}
                    disabled={action.isDisabled?.(selectedRows, data)}
                    className={`
                      flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                      ${
                        action.variant === 'primary'
                          ? 'bg-brand-600 text-white hover:bg-brand-700 disabled:bg-gray-300'
                          : action.variant === 'destructive'
                            ? 'bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-300'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:bg-gray-100'
                      }
                    `}
                  >
                    {action.icon && <span className="h-4 w-4">{action.icon}</span>}
                    <span>{action.label}</span>
                  </button>
                ))}
            </div>
          )}

          {/* View Type Switcher */}
          <div className="relative" ref={viewTypeMenuRef}>
            <button
              type="button"
              onClick={() => {
                setShowViewTypeMenu(!showViewTypeMenu);
                setShowExportMenu(false);
              }}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Switch view type"
            >
              <ViewTypeIcon className="h-4 w-4" />
              <span className="capitalize">{viewType}</span>
              <ChevronDown className="h-4 w-4" />
            </button>

            {showViewTypeMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                {Object.entries(viewTypeIcons).map(([type, Icon]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      onViewTypeChange(type as GridViewType);
                      setShowViewTypeMenu(false);
                    }}
                    className={`
                      w-full flex items-center space-x-2 px-3 py-2 text-sm text-left hover:bg-gray-100 transition-colors
                      ${viewType === type ? 'bg-brand-50 text-brand-700' : 'text-gray-700'}
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="capitalize">{type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Export */}
          {permissions.canExport && (
            <div className="relative" ref={exportMenuRef}>
              <button
                type="button"
                onClick={() => {
                  setShowExportMenu(!showExportMenu);
                  setShowViewTypeMenu(false);
                }}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Export data"
              >
                <Download className="h-4 w-4" />
                <span>Export</span>
                <ChevronDown className="h-4 w-4" />
              </button>

              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  {(['csv', 'xlsx', 'pdf'] as const).map(format => (
                    <button
                      key={format}
                      type="button"
                      onClick={() => {
                        onExport(format);
                        setShowExportMenu(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors uppercase"
                    >
                      {format}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* View Manager */}
          {permissions.canCreateView && (
            <button
              type="button"
              onClick={onViewManager}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Manage views"
            >
              <Eye className="h-4 w-4" />
              <span>Views</span>
            </button>
          )}

          {/* Column Manager */}
          <button
            type="button"
            onClick={onColumnManager}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Manage columns"
          >
            <Settings2 className="h-4 w-4" />
            <span>Columns</span>
          </button>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-3 flex items-center space-x-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">Active filters:</span>
          <div className="flex items-center space-x-2 flex-wrap">
            {Object.entries(filterValues).map(([key, value]) => {
              if (!value || value === '' || (Array.isArray(value) && value.length === 0)) {
                return null;
              }

              const filter = filters.find(f => f.id === key);
              if (!filter) return null;

              const displayValue = Array.isArray(value)
                ? value.length === 1
                  ? filter.options?.find(opt => opt.value === value[0])?.label || value[0]
                  : `${value.length} items`
                : typeof value === 'string' && value.length > 20
                  ? `${value.substring(0, 20)}...`
                  : value;

              return (
                <span
                  key={key}
                  className="inline-flex items-center space-x-1 px-2 py-1 bg-brand-100 text-brand-800 text-xs rounded-full"
                >
                  <span>
                    {filter.label}: {displayValue}
                  </span>
                  <button
                    type="button"
                    onClick={() => onFilterChange(key, Array.isArray(value) ? [] : '')}
                    className="ml-1 h-3 w-3 rounded-full hover:bg-brand-200 flex items-center justify-center"
                    title={`Remove ${filter.label} filter`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
